import * as THREE from 'three';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory';
import { XRHandModelFactory } from 'three/examples/jsm/webxr/XRHandModelFactory.js';
import CameraControls from 'camera-controls';
import CustomCameraControls from '@3jss/three-controls/CustomCameraControls';
import { loadHDRImage } from '@3jss/utils/textureHelpers';

CameraControls.install({ THREE });

/*
 * Methods commented out
 * - setupAutorotate
 * */

class ThreeController {
	setupControls(camera, renderer, orbitControlsConfig) {
		this.startDistance = null;
		this.camera = camera;

		this.controls = new CustomCameraControls(
			this.camera,
			renderer.domElement,
			orbitControlsConfig,
		);

		// if (window.count > 1) {
		renderer.domElement.addEventListener('touchend', (event) => {
			this.startDistance = null;
		});

		renderer.domElement.addEventListener('touchmove', (event) => {
			if (event.touches.length !== 1) {
				event.preventDefault();
			}
			if (
				event.touches.length === 2 &&
				!(
					orbitControlsConfig?.zoom &&
					orbitControlsConfig.zoom?.mobile === false
				)
			) {
				const base = Math.abs(
					event.touches[1].pageX - event.touches[0].pageX,
				);
				const height = Math.abs(
					event.touches[1].pageY - event.touches[0].pageY,
				);
				const dist = Math.sqrt(base ** 2 + height ** 2);
				const deltaDist = this.startDistance - dist;
				const temp = this.camera.fov + deltaDist * 0.2;
				// this.camera.position.x+=1;

				if (this.controls.enableZoom) {
					if (temp > 20 && temp < 70) {
						this.camera.fov += deltaDist * 0.2;
						this.camera.updateProjectionMatrix();
					}
				}

				this.startDistance = dist;
			}
		});

		window.c = this.controls;

		this.controls.enableZoom = true;
		this.controls.maxDistance = 0.1;
		this.controls.minDistance = 0;

		return this.controls;
	}

	setupPanControls = (enablePan = false, enableRotate = true) => {
		this.controls.enablePan = enablePan;
		this.controls.enableRotate = enableRotate;

		if (enablePan) {
			this.controls.panSpeed = 75.0;
			this.controls.screenSpacePanning = true;
			if (this?.controls?.touches) {
				this.controls.touches.ONE = THREE.TOUCH.PAN;
			}
		} else {
			if (this.controls?.touches) {
				this.controls.touches.ONE = THREE.TOUCH.ROTATE;
			}
		}

		// return this.controls;
	};

	setupRotation = (enabled) => {
		if (enabled) {
			this.controls.touches.one = CameraControls.ACTION.ROTATE;
			this.controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
		} else {
			this.controls.touches.one = CameraControls.ACTION.NONE;
			this.controls.mouseButtons.left = CameraControls.ACTION.NONE;
		}
	};

	// Load HDR environment map for controllers
	async createControllerEnvMap(renderer) {
		const hdrUrl = this.xrEnvMapConfig?.url
			? this.xrEnvMapConfig?.url
			: 'https://cdn.obsess-vr.com/natural_falloff_env.hdr';

		try {
			// Load the HDR environment map
			this.controllerEnvMap = await loadHDRImage(hdrUrl, renderer);
		} catch (error) {
			console.error(
				'Failed to load HDR environment map for controllers:',
				error,
			);
		}
	}

	// assign specific lighting for controllers on separate layers
	setupControllerLighting(controllerModel, layerID) {
		// Use HemisphereLight for soft, even lighting from all angles
		// This simulates ambient sky/ground lighting without harsh brightness
		const hemisphereLight = new THREE.HemisphereLight(
			0xffffff, // sky color
			0x444444, // ground color
			0.6, // intensity
		);

		controllerModel?.add(hemisphereLight);
		hemisphereLight.layers.set(layerID);
		controllerModel?.layers.set(layerID);

		return hemisphereLight;
	}

	setupVRControls(renderer, dolly, scene, xrEnvMapConfig) {
		const controllerModelFactory = new XRControllerModelFactory();
		const handModelFactory = new XRHandModelFactory();

		const geometry = new THREE.BufferGeometry().setFromPoints([
			new THREE.Vector3(0, 0, 0),
			new THREE.Vector3(0, 0, -1),
		]);

		// This is a temporary helper line to let the user aim at the objects in dolly
		const line = new THREE.Line(geometry);
		line.name = 'line';
		line.scale.z = 0;

		// Marker to detect intersections with 3D scenes
		const marker = new THREE.Mesh(
			new THREE.CircleGeometry(0.25, 32).rotateX(-Math.PI / 2),
			new THREE.MeshBasicMaterial({ color: 0xbcbcbc }),
		);
		marker.name = 'XR_Teleport_Marker';
		marker.visible = false;
		scene.add(marker);

		const vrControllers = []; // We return an arra for the 2 controllers
		const gripControls = [];
		const vrHands = [];
		const handModels = [];
		const controllerModels = [];

		// Initialize arrays to track loaded models that are waiting for env map
		this.pendingControllerModels = [];
		this.controllerEnvMapLoaded = false;

		this.xrEnvMapConfig = xrEnvMapConfig;

		// Start loading the dedicated environment map for controllers (async)
		// When it loads, apply it to any models that are already loaded
		this.createControllerEnvMap(renderer).then(() => {
			this.controllerEnvMapLoaded = true;
			// Apply env map to any models that loaded while we were waiting
			if (this.pendingControllerModels.length > 0) {
				this.pendingControllerModels.forEach((model) => {
					this.addRenderOrderForControllerModels(model);
				});
				this.pendingControllerModels = [];
			}
		});

		for (let i = 0; i <= 1; i++) {
			const controller = renderer.xr.getController(i);
			controller.add(line.clone());
			controller.userData.selectPressed = false;
			dolly.add(controller);

			vrControllers.push(controller);

			const grip = renderer.xr.getControllerGrip(i);
			grip.name = 'XRControllerGrip';
			const controllerModel =
				controllerModelFactory.createControllerModel(grip);

			controllerModels.push(controllerModel);
			grip.add(controllerModel);
			dolly.add(grip);

			gripControls.push(grip);

			// Hands
			const hand = renderer.xr.getHand(i);
			hand.name = 'XRHand';
			const model = handModelFactory.createHandModel(hand, 'mesh');
			hand.add(model);
			handModels.push(model);
			dolly.add(hand);

			vrHands.push(hand);
		}

		// check if the models are loaded and apply env map
		const checkForModelSuccess = (modelsRef, onModelLoaded) => {
			const maxAttempts = 120; // 30 seconds timeout (120 * 250ms)
			let attempts = 0;

			const intervalId = setInterval(() => {
				attempts++;
				let isModelLoaded = true;

				modelsRef.forEach((model) => {
					if (model.children.length === 0) {
						isModelLoaded = false; // one of the models is not ready
					}
				});

				if (isModelLoaded) {
					modelsRef.forEach((model) => {
						if (onModelLoaded) {
							onModelLoaded(model);
						}
					});
					clearInterval(intervalId); // stop once models are ready
				} else if (attempts >= maxAttempts) {
					console.warn(
						'VR controller/hand models failed to load within timeout period',
					);
					clearInterval(intervalId); // stop polling after timeout
				}
			}, 250); // check every .25s
		};

		// Apply env map for controller models when they load
		if (controllerModels?.length > 0) {
			checkForModelSuccess(
				controllerModels,
				this.addRenderOrderForControllerModels,
			);
		}

		// Apply env map for hand models when they load
		if (handModels?.length > 0) {
			checkForModelSuccess(
				handModels,
				this.addRenderOrderForControllerModels,
			);
		}

		return { vrControllers, gripControls, vrHands, handModels };
	}

	addRenderOrderForControllerModels = (controllerModel) => {
		// If env map hasn't loaded yet, add this model to pending list
		if (!this.controllerEnvMapLoaded) {
			this.pendingControllerModels.push(controllerModel);
			return;
		}

		const intensity = this.xrEnvMapConfig?.intensity
			? this.xrEnvMapConfig?.intensity
			: 0.6;

		controllerModel.traverse((child) => {
			if (child.isMesh) {
				// Set high render order to ensure controllers render on top
				child.renderOrder = 9999;

				// Apply dedicated environment map for realistic lighting
				if (child.material) {
					// Use the dedicated controller environment map if available
					if (this.controllerEnvMap) {
						child.material.envMap = this.controllerEnvMap;
						child.material.envMapIntensity = intensity;
					} else {
						// Fallback: use setupControllerLighting if env map is not available
						console.warn(
							'Controller environment map not available, using light fallback',
						);
						this.setupControllerLighting(controllerModel, 0);
					}

					// Ensure proper depth testing so controllers are visible
					child.material.depthTest = true;
					child.material.depthWrite = true;
					child.material.needsUpdate = true;
				}
			}
		});
	};

	/**
	 * Cleans up VR-related state to prevent issues when VR is disabled/re-enabled.
	 * Should be called when exiting VR mode or disposing of VR resources.
	 */
	cleanupVRState() {
		// Clear pending controller models array
		this.pendingControllerModels = [];
		this.controllerEnvMapLoaded = false;
		this.xrEnvMapConfig = null;

		// Dispose of the controller environment map texture to free GPU memory
		if (this.controllerEnvMap) {
			this.controllerEnvMap.dispose();
			this.controllerEnvMap = null;
		}
	}
}

export default new ThreeController();
